import type { Edge, Node } from '@xyflow/react';
import {
  formatAdRelationshipDisplayLabel,
  type InternalNetworkAttackPathEventDto,
  type InternalNetworkAttackPathTimelineDto,
} from '@terra/shared-contracts-internal-network';
import type {
  AttackPathChainStatus,
  AttackPathEventEdgeData,
  AttackPathEventNodeData,
} from './attack-path-event-node.types';
import { buildAttackPathEventNodeData } from './attack-path-event-node-data.util';
import { sanitizeAttackPathEvents } from './sanitize-attack-path-events.util';
import {
  attackPathElapsedSeconds,
  resolveAttackPathStartMs,
} from './attack-path-timeline-clock.util';

const YIELD_EVENT_KINDS = new Set(['CREDENTIAL_OBTAINED', 'IMPACT_ACHIEVED']);

const resolveAttackPathEdgeLabel = ({
  source,
  target,
}: {
  source: InternalNetworkAttackPathEventDto;
  target: InternalNetworkAttackPathEventDto;
}): string | null => {
  const rawTechnique =
    source.kind === 'WEAKNESS_EXPLOITED' && YIELD_EVENT_KINDS.has(target.kind)
      ? source.status === 'SUCCEEDED'
        ? source.technique
        : null
      : target.technique;

  return rawTechnique ? formatAdRelationshipDisplayLabel(rawTechnique) : null;
};

const canPrecedeAttackPathEvent = ({
  source,
  target,
}: {
  source: InternalNetworkAttackPathEventDto;
  target: InternalNetworkAttackPathEventDto;
}): boolean => {
  if (source.status === 'FAILED') {
    return false;
  }

  if (
    source.kind === 'WEAKNESS_EXPLOITED' &&
    target.kind === 'WEAKNESS_EXPLOITED'
  ) {
    return false;
  }

  return !(
    YIELD_EVENT_KINDS.has(target.kind) &&
    source.kind === 'WEAKNESS_EXPLOITED' &&
    source.status !== 'SUCCEEDED'
  );
};

export const ATTACK_PATH_EVENT_COLUMN_WIDTH = 300;
export const ATTACK_PATH_EVENT_ROW_HEIGHT = 150;

export type AttackPathEventGraph = {
  nodes: Node<AttackPathEventNodeData>[];
  edges: Edge<AttackPathEventEdgeData>[];
  hasTiming: boolean;
  maxElapsedSeconds: number;
  columns: AttackPathEventColumnTick[];
};

export type AttackPathEventColumnTick = {
  column: number;
  x: number;
  elapsedSeconds: number;
  occurredAt: string;
};

const buildChildrenMap = (
  edges: { source: string; target: string }[],
): Map<string, string[]> => {
  const children = new Map<string, string[]>();

  for (const edge of edges) {
    const existing = children.get(edge.source) ?? [];

    existing.push(edge.target);
    children.set(edge.source, existing);
  }

  return children;
};

export const computeAttackPathEventColumns = ({
  eventIds,
  edges,
}: {
  eventIds: string[];
  edges: { source: string; target: string }[];
}): Map<string, number> => {
  const parents = new Map<string, string[]>();

  for (const edge of edges) {
    const existing = parents.get(edge.target) ?? [];

    existing.push(edge.source);
    parents.set(edge.target, existing);
  }

  const columnById = new Map<string, number>();
  const resolving = new Set<string>();

  const resolve = (id: string): number => {
    const cached = columnById.get(id);

    if (cached !== undefined) {
      return cached;
    }

    if (resolving.has(id)) {
      return 0;
    }

    resolving.add(id);
    const parentIds = parents.get(id) ?? [];
    const column =
      parentIds.length === 0
        ? 0
        : Math.max(...parentIds.map((parentId) => resolve(parentId) + 1));

    resolving.delete(id);
    columnById.set(id, column);

    return column;
  };

  for (const id of eventIds) {
    resolve(id);
  }

  return columnById;
};

export const computeAttackPathEventLanes = ({
  orderedIds,
  edges,
  columnById,
}: {
  orderedIds: string[];
  edges: { source: string; target: string }[];
  columnById: Map<string, number>;
}): Map<string, number> => {
  const children = buildChildrenMap(edges);
  const laneById = new Map<string, number>();
  let nextLane = 0;

  const assign = (id: string, lane: number): void => {
    if (laneById.has(id)) {
      return;
    }

    laneById.set(id, lane);
    const childIds = [...(children.get(id) ?? [])].sort(
      (a, b) => (columnById.get(a) ?? 0) - (columnById.get(b) ?? 0),
    );

    childIds.forEach((childId, index) => {
      if (laneById.has(childId)) {
        return;
      }

      if (index === 0) {
        assign(childId, lane);
      } else {
        nextLane += 1;
        assign(childId, nextLane);
      }
    });
  };

  for (const id of orderedIds) {
    if (!laneById.has(id)) {
      assign(id, nextLane);
      nextLane += 1;
    }
  }

  return laneById;
};

const resolveChainStatus = (
  statuses: InternalNetworkAttackPathEventDto['status'][],
): AttackPathChainStatus => {
  if (statuses.includes('FAILED')) {
    return 'BLOCKED';
  }

  if (statuses.includes('IN_PROGRESS')) {
    return 'IN_PROGRESS';
  }

  return 'COMPLETED';
};

const buildChainStatusByLane = ({
  events,
  laneById,
}: {
  events: InternalNetworkAttackPathEventDto[];
  laneById: Map<string, number>;
}): Map<number, AttackPathChainStatus> => {
  const statusesByLane = new Map<
    number,
    InternalNetworkAttackPathEventDto['status'][]
  >();

  for (const event of events) {
    const lane = laneById.get(event.id) ?? 0;
    const laneStatuses = statusesByLane.get(lane) ?? [];

    laneStatuses.push(event.status);
    statusesByLane.set(lane, laneStatuses);
  }

  return new Map(
    [...statusesByLane.entries()].map(([lane, statuses]) => [
      lane,
      resolveChainStatus(statuses),
    ]),
  );
};

export const connectOrphanAttackPathEvents = ({
  events,
  explicitEdges,
}: {
  events: InternalNetworkAttackPathEventDto[];
  explicitEdges: { source: string; target: string; label: string | null }[];
}): { source: string; target: string; label: string | null }[] => {
  const withIncoming = new Set(explicitEdges.map((edge) => edge.target));
  const edges = [...explicitEdges];

  for (let index = 1; index < events.length; index += 1) {
    const event = events[index];

    if (!event || withIncoming.has(event.id)) {
      continue;
    }

    const predecessor = [...events.slice(0, index)]
      .reverse()
      .find((candidate) =>
        canPrecedeAttackPathEvent({ source: candidate, target: event }),
      );

    if (!predecessor) {
      continue;
    }

    edges.push({
      source: predecessor.id,
      target: event.id,
      label: resolveAttackPathEdgeLabel({
        source: predecessor,
        target: event,
      }),
    });
    withIncoming.add(event.id);
  }

  return edges;
};

export const buildAttackPathEventGraph = (
  timeline: InternalNetworkAttackPathTimelineDto,
): AttackPathEventGraph => {
  const events = sanitizeAttackPathEvents(timeline.events);
  const startMs = resolveAttackPathStartMs({
    startedAt: timeline.startedAt,
    occurredAtValues: events.map((event) => event.occurredAt),
  });

  if (events.length === 0) {
    return {
      nodes: [],
      edges: [],
      hasTiming: false,
      maxElapsedSeconds: 0,
      columns: [],
    };
  }

  const eventIds = events.map((event) => event.id);
  const validIds = new Set(eventIds);
  const eventById = new Map(events.map((event) => [event.id, event]));

  const explicitEdges = events.flatMap((event) =>
    event.sourceEventIds
      .filter((sourceId) => validIds.has(sourceId))
      .flatMap((sourceId) => {
        const source = eventById.get(sourceId);

        if (!source || !canPrecedeAttackPathEvent({ source, target: event })) {
          return [];
        }

        return [
          {
            source: sourceId,
            target: event.id,
            label: resolveAttackPathEdgeLabel({
              source,
              target: event,
            }),
          },
        ];
      }),
  );

  const edges = connectOrphanAttackPathEvents({ events, explicitEdges });

  const columnById = computeAttackPathEventColumns({ eventIds, edges });
  const laneById = computeAttackPathEventLanes({
    orderedIds: eventIds,
    edges,
    columnById,
  });
  const chainStatusByLane = buildChainStatusByLane({ events, laneById });

  const maxElapsedSeconds = events.reduce(
    (max, event) =>
      Math.max(
        max,
        attackPathElapsedSeconds({ occurredAt: event.occurredAt, startMs }),
      ),
    0,
  );

  const nodes: Node<AttackPathEventNodeData>[] = events.map((event) => {
    const column = columnById.get(event.id) ?? 0;
    const lane = laneById.get(event.id) ?? 0;
    const elapsedSeconds = attackPathElapsedSeconds({
      occurredAt: event.occurredAt,
      startMs,
    });
    const chainStatus = chainStatusByLane.get(lane) ?? 'IN_PROGRESS';

    return {
      id: event.id,
      type: 'attackPathEventNode',
      position: {
        x: column * ATTACK_PATH_EVENT_COLUMN_WIDTH,
        y: lane * ATTACK_PATH_EVENT_ROW_HEIGHT,
      },
      data: buildAttackPathEventNodeData({
        event,
        column,
        lane,
        startMs,
        chainStatus,
        chainProgress:
          maxElapsedSeconds === 0 ? 0 : elapsedSeconds / maxElapsedSeconds,
      }),
    };
  });

  const flowEdges: Edge<AttackPathEventEdgeData>[] = edges.map(
    (edge, index) => ({
      id: `${edge.source}->${edge.target}:${index}`,
      source: edge.source,
      target: edge.target,
      type: 'attackPathEventEdge',
      data: {
        label: edge.label ?? null,
        variant: 'proven',
        campaignId: null,
      },
    }),
  );

  const columnTicks = buildColumnTicks({ nodes, startMs });
  const hasTiming = events.some(
    (event) =>
      attackPathElapsedSeconds({ occurredAt: event.occurredAt, startMs }) > 1,
  );

  return {
    nodes,
    edges: flowEdges,
    hasTiming,
    maxElapsedSeconds,
    columns: columnTicks,
  };
};

const buildColumnTicks = ({
  nodes,
  startMs,
}: {
  nodes: Node<AttackPathEventNodeData>[];
  startMs: number | null;
}): AttackPathEventColumnTick[] => {
  const earliestByColumn = new Map<number, AttackPathEventNodeData>();

  for (const node of nodes) {
    const existing = earliestByColumn.get(node.data.column);

    if (
      !existing ||
      new Date(node.data.occurredAt).getTime() <
        new Date(existing.occurredAt).getTime()
    ) {
      earliestByColumn.set(node.data.column, node.data);
    }
  }

  return [...earliestByColumn.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([column, data]) => ({
      column,
      x: column * ATTACK_PATH_EVENT_COLUMN_WIDTH,
      elapsedSeconds: attackPathElapsedSeconds({
        occurredAt: data.occurredAt,
        startMs,
      }),
      occurredAt: data.occurredAt,
    }));
};
